import React, {useContext, useEffect, useMemo, useState} from "react";
import Markdown, {Components} from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {CodeBlock} from "./blocks/CodeBlock";
import {useAnimatedText} from "../../hooks";
import {Citation} from "./inline/Citation";
import {ActionIcon, Anchor, Box, Modal, Stack, Table, Text} from "@mantine/core";
import classes from "./GPTStreamParser.module.css";
import {useDebouncedValue} from "@mantine/hooks";
import {
    processReferences,
    stripLCitations,
    escapeCitationBrackets,
    fixNestedMarkdownLinks,
    normalizeMathDelimiters,
    resolveUnclosedDisplayMath,
    containsMathDelimiters,
    blankOutCodeAndMath,
} from "./utils";
import {ParagraphWithReferences} from "./inline/ParagraphWithReferences";
import ExternalLinkIcon from "../../../../assets/external-link.svg?react";
import ArrowsMaximizeIcon from "../../../../assets/arrows-maximize.svg?react";
import XIcon from "../../../../assets/x.svg?react";
import {useTranslation} from "react-i18next";
import {GPTMeta, GPTMetaContext} from "./MetaProvider";
import {useNavigate} from "react-router-dom";
import {useExperimentalLinkResolution} from "../../../../components/ExperimentalLink";

// Custom components for the special tags

export const Thinking: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            m={0}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Step: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Count: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#fff5e6",
                padding: "5px",
                borderRadius: "4px",
                color: "#ff6600",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

export const Reflection: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            {processReferences(children)}
        </Box>
    );
};

export const Answer: React.FC<any> = ({children, ...props}) => {
    return <div>{processReferences(children)}</div>;
};

export const Reward: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#f0f8ff",
                padding: "5px",
                borderRadius: "4px",
                fontStyle: "italic",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

// Custom link component for app.dib.no URLs
const DibAppLink: React.FC<{href: string; children: React.ReactNode}> = ({
    href,
    children,
}) => {
    const context = useContext(GPTMetaContext);
    const navigate = useNavigate();
    const {effectiveHref, internalPath} = useExperimentalLinkResolution(href);
    const isDibAppLink =
        effectiveHref?.includes("app.dib.no") ||
        effectiveHref?.startsWith("/dibappapi/");

    const toolTitle = useMemo(() => {
        if (
            context?.relatedTools === undefined ||
            context?.relatedTools.length === 0
        ) {
            return undefined;
        }
        for (const tool of context?.relatedTools) {
            if (tool.url === href) {
                return tool.title;
            }
        }
        return undefined;
    }, [context?.relatedTools]);

    const usedSourceTitle = useMemo(() => {
        if (context?.sources === undefined || context?.sources.length === 0) {
            return undefined;
        }
        for (const source of context?.sources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    const consideredSourceTitle = useMemo(() => {
        if (
            context?.consideredSources === undefined ||
            context?.consideredSources.length === 0
        ) {
            return undefined;
        }
        for (const source of context?.consideredSources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    // Differentiate between plain links without title and titled markdown links
    // Plain links will be an array with href at index 0
    const altTitle =
        Array.isArray(children) && children.length > 0 && href === children[0]
            ? "app.dib"
            : children;

    const displayTitle =
        toolTitle || usedSourceTitle || consideredSourceTitle || altTitle;

    // Keep the display of this anchor at its default value, inline. inline-flex and
    // inline-block make the anchor a single box that cannot break across lines. A long
    // title then moves to the next line as a whole instead of wrapping.
    if (isDibAppLink && internalPath) {
        return (
            <Anchor
                href={internalPath}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate(internalPath, {viewTransition: true});
                }}
            >
                {displayTitle}
            </Anchor>
        );
    }

    // Keep the display of this anchor at its default value, inline. inline-flex and
    // inline-block make the anchor a single box that cannot break across lines. A long
    // title then moves to the next line as a whole instead of wrapping. flex makes the
    // anchor block-level, which puts the link on a line of its own. vertical-align
    // aligns the icon with the text.
    if (isDibAppLink) {
        return (
            <Anchor
                href={effectiveHref}
                target="_blank"
                rel="noopener noreferrer"
            >
                {displayTitle}
                <ExternalLinkIcon
                    width={16}
                    height={16}
                    style={{
                        verticalAlign: "text-bottom",
                        marginLeft: "4px",
                    }}
                />
            </Anchor>
        );
    }

    return (
        <Anchor href={effectiveHref} target="_blank" rel="noopener noreferrer">
            {children}
        </Anchor>
    );
};

type Props = {
    text: string;
    meta: GPTMeta;
    /**
     * The time in milliseconds between each update
     */
    delta?: number;
    className?: string;
    hideCitations?: boolean;
    options?: {
        includeCustomComponents?: {
            thinking?: boolean;
            step?: boolean;
            count?: boolean;
            reflection?: boolean;
            answer?: boolean;
            reward?: boolean;
            code?: boolean;
            references?: boolean;
        };
        chunkSize?: number;
    };
    onFinish?: () => void;
};

// A wrapped component generator used for most markdown elements that must have references processed.
const createWrappedComponent = (Component: React.ElementType) => {
    return ({node, children, ...props}: any) => {
        const processedChildren = processReferences(children);
        return <Component {...props}>{processedChildren}</Component>;
    };
};

// Custom table components using Mantine's Table for better styling
const CustomTable: React.FC<any> = ({node, children, ...props}) => {
    const [expanded, setExpanded] = useState(false);
    const {t} = useTranslation();

    const tableContent = (
        <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            className={classes.table}
            {...props}
        >
            {children}
        </Table>
    );

    return (
        <>
            <Box className={classes.tableWrapper}>
                {tableContent}
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    className={classes.expandButton}
                    onClick={() => setExpanded(true)}
                    title={t("table.expand")}
                >
                    <ArrowsMaximizeIcon width={14} height={14} />
                </ActionIcon>
            </Box>

            <Modal
                opened={expanded}
                onClose={() => setExpanded(false)}
                size="xl"
                fullScreen
                classNames={{
                    body: classes.modalBody,
                    content: classes.modalContent,
                }}
                withCloseButton={false}
            >
                <Box
                    className={classes.expandedTableContainer}
                    onClick={() => setExpanded(false)}
                >
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        className={classes.closeButton}
                        onClick={() => setExpanded(false)}
                        title={t("table.close")}
                    >
                        <XIcon width={18} height={18} />
                    </ActionIcon>
                    <Box
                        className={classes.expandedTableScroll}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            withColumnBorders
                            className={classes.expandedTable}
                            {...props}
                        >
                            {children}
                        </Table>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

const CustomThead: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Thead {...props}>{children}</Table.Thead>;
};

const CustomTbody: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tbody {...props}>{children}</Table.Tbody>;
};

const CustomTr: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tr {...props}>{children}</Table.Tr>;
};

const CustomTh: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Th {...props}>{processedChildren}</Table.Th>;
};

const CustomTd: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Td {...props}>{processedChildren}</Table.Td>;
};

/**
 * CustomParagraph checks if its sole child is one of our custom elements.
 * If so, it returns that element directly (so that it isn't wrapped in <p></p>).
 * Otherwise, it falls back to a normal paragraph.
 */
const CustomParagraph: React.FC<any> = ({node, children, ...props}) => {
    const childArray = React.Children.toArray(children);

    // If the paragraph contains exactly one child AND that child is one of our custom tags,
    // then return it directly
    if (childArray.length === 1 && React.isValidElement(childArray[0])) {
        const element = childArray[0];
        const type = element.type;

        // Check against both string types and our imported component references.
        if (
            type === "thinking" ||
            type === Thinking ||
            type === "step" ||
            type === Step ||
            type === "count" ||
            type === Count ||
            type === "reflection" ||
            type === Reflection ||
            type === "answer" ||
            type === Answer ||
            type === "reward" ||
            type === Reward ||
            type === DibAppLink
        ) {
            return <>{element}</>;
        }
    }

    // Otherwise, render a normal <p> with processed references.
    return <ParagraphWithReferences>{children}</ParagraphWithReferences>;
};

const Nada = () => <></>;

// KaTeX and its stylesheet add about 90 KB. They stay out of the initial bundle and load
// only for an answer that contains math. The promise below keeps the load to one.
const importMathPlugins = () =>
    Promise.all([
        import("remark-math"),
        import("rehype-katex"),
        import("katex/dist/katex.min.css"),
    ]).then(([remarkMath, rehypeKatex]) => ({
        remark: remarkMath.default,
        rehype: rehypeKatex.default,
    }));

type MathPlugins = Awaited<ReturnType<typeof importMathPlugins>>;

let mathPluginsRequest: Promise<MathPlugins> | undefined;

/**
 * Starts the shared load, or returns the load that is in progress. A failed load is not
 * kept. The next answer with math starts a new load.
 */
const loadMathPlugins = (): Promise<MathPlugins> => {
    mathPluginsRequest ??= importMathPlugins().catch((error) => {
        mathPluginsRequest = undefined;
        throw error;
    });
    return mathPluginsRequest;
};

/**
 * Returns the math plugins when the text contains math and the load is complete. Returns
 * undefined before that. Without the plugins the math stays plain text.
 */
const useMathPlugins = (text: string): MathPlugins | undefined => {
    const [plugins, setPlugins] = useState<MathPlugins>();
    const hasMath = containsMathDelimiters(text);

    useEffect(() => {
        if (!hasMath) {
            return;
        }
        void loadMathPlugins().then(setPlugins, (error) => {
            console.error("Could not load KaTeX, math stays plain text:", error);
        });
    }, [hasMath]);

    return plugins;
};

// The HTML parser in rehype-raw opens a tag only when a letter, "/", "!", or "?" follows "<".
// The parser reads "<?" as a comment that includes all text up to the next ">".
const TAG_OPENER = /<[A-Za-z/!?]/;
// A trailing "<" in a stream can still become the start of a tag.
const TAG_OPENER_OR_END = /<([A-Za-z/!?]|$)/;

/** Removes the text from a "<" that opens a tag with no ">" after it. */
const stripUnclosedCustomTags = (text: string, isComplete: boolean): string => {
    // Code and formulas hold "<" and ">" characters that open and close no tag.
    const searchable = blankOutCodeAndMath(text);
    // The last ">" closes every opener before it, even a ">" inside code or a formula.
    // The search reads the original text and not the blanked copy.
    const searchFrom = text.lastIndexOf(">") + 1;
    const opener = searchable
        .slice(searchFrom)
        .search(isComplete ? TAG_OPENER : TAG_OPENER_OR_END);
    return opener === -1 ? text : text.slice(0, searchFrom + opener);
};

// Keep this order. escapeCitationBrackets turns [1] into \[1\], and normalizeMathDelimiters
// reads \[1\] as math. resolveUnclosedDisplayMath needs the delimiter that the model wrote.
const ANSWER_TEXT_STEPS: ((text: string, isComplete: boolean) => string)[] = [
    stripUnclosedCustomTags,
    stripLCitations,
    fixNestedMarkdownLinks,
    resolveUnclosedDisplayMath,
    normalizeMathDelimiters,
    escapeCitationBrackets,
];

const prepareAnswerText = (text: string, isComplete: boolean): string =>
    ANSWER_TEXT_STEPS.reduce(
        (prepared, step) => step(prepared, isComplete),
        text
    );

// Main component that processes and renders markdown
export const GPTStreamParser: React.FC<Props> = ({
    text,
    meta,
    delta = 5,
    className,
    hideCitations,

    options = {
        chunkSize: 3,
        includeCustomComponents: {
            thinking: true,
            step: true,
            count: true,
            reflection: true,
            answer: true,
            reward: true,
            code: true,
            references: true,
        },
    },
    onFinish,
}) => {
    const displayText = useAnimatedText(text, {
        delta,
        chunkSize: 3,
        adaptive: true,
    });

    // Look at the full text, not the animated part, so the load starts while it streams.
    const mathPlugins = useMathPlugins(text);

    // The answer is complete when the thread stops streaming and the animation shows all of
    // the text. The text prop grows while the backend streams. The animation reaches the end
    // of the text many times before the answer is complete.
    const isComplete = !meta?.isStreaming && displayText.length === text.length;

    // Debug
    /* const [debouncedText] = useDebouncedValue(text, 100);
    useEffect(() => {
        if (debouncedText) {
            console.log(`Base text before parsing: \n${debouncedText}`);
        }
    }, [debouncedText]); */

    useEffect(() => {
        if (!onFinish) {
            return;
        }

        if (text && displayText.length === text.length) {
            onFinish();
        }
    }, [onFinish, displayText, text]);

    return (
        <Box className={`${classes.container} ${className} ${hideCitations ? classes.hideCitations : ""}`}>
            <GPTMetaContext.Provider value={meta}>
                <Markdown
                    remarkPlugins={[remarkGfm, ...(mathPlugins ? [mathPlugins.remark] : [])]}
                    rehypePlugins={
                        // rehype-katex must come after rehype-raw, which rebuilds the tree.
                        [rehypeRaw, ...(mathPlugins ? [mathPlugins.rehype] : [])]
                    }
                    components={
                        {
                            li: createWrappedComponent("li"),
                            // Use our custom paragraph to "unwrap" custom blocks if detected.
                            p: CustomParagraph,
                            /* p: createWrappedComponent("p"), */
                            strong: createWrappedComponent("strong"),
                            em: createWrappedComponent("em"),
                            blockquote: createWrappedComponent("blockquote"),
                            // Custom table components with Mantine styling and reference processing
                            table: CustomTable,
                            thead: CustomThead,
                            tbody: CustomTbody,
                            tr: CustomTr,
                            th: CustomTh,
                            td: CustomTd,
                            // Map our custom HTML tags to the appropriate components.
                            references: options.includeCustomComponents
                                ?.references
                                ? CodeBlock
                                : Nada,
                            thinking: options.includeCustomComponents?.thinking
                                ? Thinking
                                : Nada,
                            step: options.includeCustomComponents?.step
                                ? Step
                                : Nada,
                            count: options.includeCustomComponents?.count
                                ? Count
                                : Nada,
                            reflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            finalreflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            answer: options.includeCustomComponents?.answer
                                ? Answer
                                : Nada,
                            reward: options.includeCustomComponents?.reward
                                ? Reward
                                : Nada,
                            a: DibAppLink,
                        } as Components
                    }
                >
                    {prepareAnswerText(displayText || text, isComplete)}
                </Markdown>
            </GPTMetaContext.Provider>
        </Box>
    );
};
